import { FC, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { Grid } from '@mui/material';
import clsx from 'clsx';

import { NumberInputControlled } from 'components/_basic/Form/NumberInput';
import { ReadOnlyNumberInput } from 'components/_basic/Form/NumberInput/ReadOnlyNumberInput';
import { TooltipComponent } from 'components/_basic/TooltipComponent';
import { IPeriodsTableRow } from 'components/BusinessToolForm/constants/periods';
import { isHighlightedPeriodColumn } from 'components/BusinessToolForm/utils/financialCalculations';
import { reCalculatedPeriodValues } from 'components/BusinessToolForm/utils/helpers';

import { concatWithDot } from 'helpers/string';

import { IFieldTableRowProps } from './types';
import { FieldTableRowTitle } from '../FieldTableRowTitle';
import { FieldTableWatchedRowTitle } from '../FieldTableRowTitle';

import styles from './FieldTableRow.module.scss';

const defaultGetRowName = (name: string, columnName: string, rowIndex?: number) => {
  return concatWithDot(name, rowIndex?.toString() || '', columnName);
};

export const FieldTableRow: FC<IFieldTableRowProps> = ({
  name,
  rowIndex,
  columnsNames,
  disabled = false,
  rowTitle,
  watchRowTitleName = '',
  titleClass = '',
  firstColumnWidth,
  getRowName = defaultGetRowName,
  readOnly = false,
  reCalculationHandler,
  dataAttributeWithIndex = false,
  needToConvertToNegative = false,
  allowNegative = true,
  decimalScale = 2,
  readOnlyFirstCell = false,
  recalculateOnChangeRowIndexes = [],
  highlightedColumns,
  highlightedColumnsTooltip,
}) => {
  const [isNeedToRecalculate, setIsNeedToRecalculate] = useState(false);
  const oldValue = useRef<IPeriodsTableRow[]>([]);

  const { control, getValues, setValue } = useFormContext();
  const { update } = useFieldArray({ control, name });

  if (!isNeedToRecalculate) {
    oldValue.current = structuredClone(getValues(name));
  }

  const handleUpdate = (fieldName: string) => {
    const values = getValues(name);
    const updatedPeriodValues = reCalculatedPeriodValues(fieldName, values[rowIndex]);
    update(rowIndex, updatedPeriodValues);
  };

  const onBlurHandler = async (fieldName: string, fieldValue: string) => {
    const numberValue = parseFloat(fieldValue.replace(/,/g, ''));
    if (needToConvertToNegative && numberValue > 0) {
      setValue(fieldName, -numberValue);
    }
    if (
      ((!recalculateOnChangeRowIndexes.length || !recalculateOnChangeRowIndexes.includes(rowIndex)) &&
        !isNeedToRecalculate) ||
      readOnly
    )
      return;
    if (reCalculationHandler) {
      await reCalculationHandler(rowIndex, fieldName, oldValue.current.at(rowIndex));
      setIsNeedToRecalculate(false);
      return;
    }

    handleUpdate(fieldName);
    setIsNeedToRecalculate(false);
  };

  return (
    <Grid container spacing={1} sx={{ marginBottom: '12px' }} wrap="nowrap" gap={2}>
      <Grid
        item
        sx={{ width: firstColumnWidth, minWidth: firstColumnWidth }}
        className={clsx(styles.titleWrapper, titleClass)}
      >
        {watchRowTitleName && (
          <FieldTableWatchedRowTitle
            watchRowTitleName={watchRowTitleName.replace(':index', String(rowIndex))}
            disabled={disabled}
          />
        )}
        {rowTitle && !watchRowTitleName && <FieldTableRowTitle title={rowTitle} />}
      </Grid>
      <Grid item xs>
        <Grid container spacing={1} columns={columnsNames.length} wrap="nowrap">
          {columnsNames.map((columnName, columnIndex) => {
            const fieldName = getRowName(name, columnName, rowIndex);
            const isHighlighted = isHighlightedPeriodColumn(columnName, highlightedColumns);
            const numberInput = readOnly || (readOnlyFirstCell && columnIndex === 0) ? (
              <ReadOnlyNumberInput
                id={fieldName}
                name={fieldName}
                disabled={disabled}
                className={styles.rowInput}
                decimalScale={decimalScale}
                allowNegative
              />
            ) : (
              <NumberInputControlled
                control={control}
                id={fieldName}
                name={fieldName}
                disabled={disabled}
                className={clsx(styles.rowInput, isHighlighted && styles.highlightedCell)}
                onBlur={({ target: { value } }) =>
                  !recalculateOnChangeRowIndexes.length || !recalculateOnChangeRowIndexes.includes(rowIndex)
                    ? onBlurHandler(fieldName, value)
                    : null
                }
                onChange={() =>
                  recalculateOnChangeRowIndexes.length && recalculateOnChangeRowIndexes.includes(rowIndex)
                    ? handleUpdate(fieldName)
                    : setIsNeedToRecalculate(true)
                }
                data-mirror-field={dataAttributeWithIndex ? concatWithDot(name, String(rowIndex)) : name}
                allowNegative={allowNegative}
              />
            );

            return (
              <Grid key={columnName} item xs={1}>
                {isHighlighted && highlightedColumnsTooltip ? (
                  <TooltipComponent title={highlightedColumnsTooltip} placement="top">
                    <div className={styles.highlightedInputWrapper}>{numberInput}</div>
                  </TooltipComponent>
                ) : (
                  numberInput
                )}
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    </Grid>
  );
};
